import Web3 from 'web3';
import { baseConfig } from '../config/settings';
import { parseChainId } from './chainId';
import { getInjectedProvider } from './walletProvider';

// Ordered by reliability/generosity. The official Base endpoint and PublicNode
// tolerate the dapp's polling bursts; drpc.org's free tier rate-limits (429)
// aggressively, so it sits last as a fallback only. Deduped in case a URL also
// appears in baseConfig.rpcUrls.
const BASE_RPC_URLS = Array.from(
  new Set([
    'https://mainnet.base.org',
    'https://base-rpc.publicnode.com',
    'https://base-mainnet.public.blastapi.io',
    'https://base.drpc.org',
    ...baseConfig.rpcUrls,
  ])
);

let cachedReadWeb3 = null;
let rpcStartIndex = 0;

// A public RPC that accepts the connection but never responds would otherwise
// hang a read forever. Bound every probe/read so a slow endpoint fails fast and
// we rotate to the next one instead of leaving the UI stuck on "loading".
const RPC_PROBE_TIMEOUT_MS = 5000;
const RPC_READ_TIMEOUT_MS = 12000;

/** Reject with a transient-looking error if `promise` doesn't settle in `ms`. */
function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('RPC request timeout')), ms);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/** Detect rate-limit / transient network errors worth retrying on another RPC. */
export function isTransientRpcError(error) {
  const msg = (error?.message || String(error || '')).toLowerCase();
  return (
    msg.includes('too many request') ||
    msg.includes('rate limit') ||
    msg.includes('429') ||
    msg.includes('failed to fetch') ||
    msg.includes('invalid json') ||
    msg.includes('timeout') ||
    msg.includes('network error') ||
    msg.includes('could not') ||
    msg.includes('connection')
  );
}

/** Pick a working public Base RPC (probes endpoints, starting after any failed one). */
export async function getReliableReadWeb3() {
  if (cachedReadWeb3) return cachedReadWeb3;

  let lastError;
  for (let i = 0; i < BASE_RPC_URLS.length; i++) {
    const rpcUrl = BASE_RPC_URLS[(rpcStartIndex + i) % BASE_RPC_URLS.length];
    try {
      const web3 = new Web3(rpcUrl);
      await withTimeout(web3.eth.getBlockNumber(), RPC_PROBE_TIMEOUT_MS);
      cachedReadWeb3 = web3;
      return web3;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Unable to reach Base network');
}

/** Drop the current RPC and advance so the next probe picks a different endpoint. */
export function rotateReadWeb3() {
  rpcStartIndex = (rpcStartIndex + 1) % BASE_RPC_URLS.length;
  cachedReadWeb3 = null;
}

/**
 * Run a read against a working RPC, rotating to a fresh endpoint and retrying
 * when the call fails with a transient (rate-limit / network) error.
 * `fn` receives the web3 instance and should return its result.
 */
export async function readWithFailover(fn, { retries = BASE_RPC_URLS.length } = {}) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    let web3;
    try {
      web3 = await getReliableReadWeb3();
    } catch (error) {
      lastError = error;
      rotateReadWeb3();
      continue;
    }
    try {
      return await withTimeout(fn(web3), RPC_READ_TIMEOUT_MS);
    } catch (error) {
      lastError = error;
      if (isTransientRpcError(error)) {
        rotateReadWeb3();
        continue;
      }
      throw error;
    }
  }
  throw lastError || new Error('Unable to reach Base network');
}

/** The active injected EIP-1193 provider (EIP-6963 aware), or null. */
export function getWalletProvider() {
  return getInjectedProvider();
}

/** Wallet provider — used only for sending transactions. */
export function getWalletWeb3() {
  const provider = getInjectedProvider();
  if (!provider) return null;
  return new Web3(provider);
}

export async function getAccount(web3) {
  const w3 = web3 || getWalletWeb3();
  if (!w3) return null;
  const accounts = await w3.eth.getAccounts();
  return accounts[0] || null;
}

/** Read chain ID from the wallet — prefer EIP-1193 eth_chainId (MetaMask per-dapp network). */
export async function getWalletChainId() {
  const provider = getInjectedProvider();
  if (provider?.request) {
    try {
      return await provider.request({ method: 'eth_chainId' });
    } catch {
      /* fall through */
    }
  }

  const web3 = getWalletWeb3();
  if (!web3) return null;
  try {
    return await web3.eth.getChainId();
  } catch {
    try {
      return await web3.eth.net.getId();
    } catch {
      return null;
    }
  }
}

/**
 * Whether the wallet is on Base mainnet.
 * Returns true when chain ID is unknown (let the wallet enforce network on tx).
 * Only returns false when we positively detect a different chain.
 */
export async function isWalletOnBase() {
  const chainId = await getWalletChainId();
  const parsed = parseChainId(chainId);
  if (!Number.isFinite(parsed)) return true;
  return parsed === parseChainId(baseConfig.chainId);
}

/** Chain status for UI — distinguish "unknown" from "confirmed wrong network". */
export async function getWalletBaseStatus() {
  const chainId = await getWalletChainId();
  const parsed = parseChainId(chainId);
  if (!Number.isFinite(parsed)) {
    return { onBase: true, known: false, chainId: null };
  }
  const onBase = parsed === parseChainId(baseConfig.chainId);
  return { onBase, known: true, chainId: parsed };
}
